import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { startAuthentication } from '@simplewebauthn/browser'
import { useOptimistic, useState } from 'react'
import {
	data,
	Form,
	href,
	Link,
	useNavigate,
	useSearchParams,
} from 'react-router'
import { z } from 'zod'
import {
	AuthDivider,
	AuthField,
	AuthShell,
	AuthSubmit,
} from '#app/components/auth/auth-shell.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Honeypot } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { enforceAbuseLimit } from '#app/utils/abuse-limit.server.ts'
import {
	login,
	requireAnonymous,
	type LoginResult,
} from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getErrorMessage, useIsPending } from '#app/utils/misc.tsx'
import { getIpAddress } from '#app/utils/request-info.server.ts'
import { PasswordSchema, EmailSchema } from '#app/utils/user-validation.ts'
import { noIndexMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/login.ts'
import { handleNewSession, handleTwoFactorRedirect } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

const AuthenticationOptionsSchema = z.object({
	options: z.object({ challenge: z.string() }),
}) satisfies z.ZodType<{ options: PublicKeyCredentialRequestOptionsJSON }>

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)

	await enforceAbuseLimit('login', getIpAddress(request))

	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (formValues, ctx) => {
				if (intent !== null)
					return { ...formValues, loginResult: null as LoginResult }

				const result = await login({
					email: formValues.email,
					password: formValues.password,
					rememberMe: formValues.remember,
					request,
				})

				if (result && 'error' in result) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: result.lockout.reason,
					})
					return z.NEVER
				}

				if (!result) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid email or password',
					})
					return z.NEVER
				}

				return { ...formValues, loginResult: result }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.loginResult) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { loginResult, redirectTo } = submission.value

	if ('twoFactorRedirect' in loginResult) {
		return handleTwoFactorRedirect(
			loginResult,
			redirectTo,
			submission.value.email,
		)
	}

	if ('error' in loginResult) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: 400 },
		)
	}

	return handleNewSession({
		request,
		session: loginResult,
		redirectTo,
	})
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthShell
			eyebrow="/ login"
			title={
				<>
					Welcome <span className="accent-underline">back</span>.
				</>
			}
			intro="Sign in to your account to manage scans, websites, and reports."
			footer={
				<>
					Don't have an account?{' '}
					<Link
						to={
							redirectTo
								? {
										pathname: href('/signup'),
										search: `redirectTo=${encodeURIComponent(redirectTo)}`,
									}
								: href('/signup')
						}
						className="font-medium text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
					>
						Create one
					</Link>
					.
				</>
			}
		>
			<Form method="POST" {...getFormProps(form)}>
				<Honeypot />

				<AuthField
					id={fields.email.id}
					label="Email"
					error={fields.email.errors?.[0]}
				>
					<input
						{...getInputProps(fields.email, { type: 'email' })}
						autoFocus
						autoComplete="email"
						placeholder="you@example.com"
						className="auth-input lowercase"
					/>
				</AuthField>

				<AuthField
					id={fields.password.id}
					label="Password"
					error={fields.password.errors?.[0]}
					hint={
						<Link
							to={href('/forgot-password')}
							className="inline-flex min-h-6 items-center text-[12px] text-[var(--marketing-accent)] underline-offset-4 transition-colors hover:text-[var(--marketing-accent-light)] hover:underline"
						>
							Forgot?
						</Link>
					}
				>
					<input
						{...getInputProps(fields.password, { type: 'password' })}
						autoComplete="current-password"
						placeholder="••••••••••"
						className="auth-input"
					/>
				</AuthField>

				{/* The label is the tap target, and it measured 20px tall - under the
				    24px WCAG 2.2 AA floor, and well under a comfortable thumb on a
				    phone. Same `min-h-11 / md:min-h-6` step the marketing header
				    already uses. */}
				<label className="mb-5 inline-flex min-h-11 cursor-pointer items-center gap-2.5 text-[13px] text-[var(--marketing-gray)] md:min-h-6">
					<input
						{...getInputProps(fields.remember, { type: 'checkbox' })}
						className="auth-checkbox"
					/>
					Keep me signed in on this device
				</label>

				<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
				<ErrorList errors={form.errors} id={form.errorId} announce />

				<AuthSubmit isPending={isPending} pendingLabel="Signing in…">
					Sign in
				</AuthSubmit>
			</Form>

			<AuthDivider>or</AuthDivider>

			<div className="space-y-2.5">
				<PasskeyLogin
					redirectTo={redirectTo}
					remember={fields.remember.value === 'on'}
				/>
				{providerNames.map((providerName) => (
					<ProviderConnectionForm
						key={providerName}
						type="Login"
						providerName={providerName}
						redirectTo={redirectTo}
						className="auth-secondary-btn"
					/>
				))}
			</div>
		</AuthShell>
	)
}

const VerificationResponseSchema = z.discriminatedUnion('status', [
	z.object({
		status: z.literal('success'),
		location: z.string(),
	}),
	z.object({
		status: z.literal('error'),
		error: z.string(),
	}),
])

function PasskeyLogin({
	redirectTo,
	remember,
}: {
	redirectTo: string | null
	remember: boolean
}) {
	// `useTransition()`'s flag only reports transitions started by its own
	// `startTransition`, which this component never called - so the button was
	// permanently idle and permanently enabled, and a second click could start a
	// second WebAuthn ceremony mid-flight. The real signal is whether the async
	// action is still running, which only this component knows.
	const [isPending, setIsPending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [passkeyMessage, setPasskeyMessage] = useOptimistic<string | null>(
		'Sign in with a passkey',
	)
	const navigate = useNavigate()

	async function handlePasskeyLogin() {
		setIsPending(true)
		setError(null)
		try {
			setPasskeyMessage('Generating authentication options…')
			const optionsResponse = await fetch(href('/webauthn/authentication'))
			const json = await optionsResponse.json()
			const { options } = AuthenticationOptionsSchema.parse(json)

			setPasskeyMessage('Requesting your authorisation…')
			const authResponse = await startAuthentication({ optionsJSON: options })
			setPasskeyMessage('Verifying your passkey…')

			const verificationResponse = await fetch(
				href('/webauthn/authentication'),
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ authResponse, remember, redirectTo }),
				},
			)

			const verificationJson = await verificationResponse.json().catch(() => ({
				status: 'error',
				error: 'Unknown error',
			}))

			const parsedResult =
				VerificationResponseSchema.safeParse(verificationJson)
			if (!parsedResult.success) {
				throw new Error(parsedResult.error.message)
			} else if (parsedResult.data.status === 'error') {
				throw new Error(parsedResult.data.error)
			}
			const { location } = parsedResult.data

			setPasskeyMessage("You're signed in. Redirecting…")
			await navigate(location ?? href('/dashboard'))
		} catch (e) {
			const errorMessage = getErrorMessage(e)
			setError(`Passkey sign-in failed: ${errorMessage}`)
		} finally {
			setIsPending(false)
		}
	}

	return (
		<form action={handlePasskeyLogin}>
			<StatusButton
				id="passkey-login-button"
				aria-describedby="passkey-login-button-error"
				className="auth-secondary-btn"
				status={isPending ? 'pending' : error ? 'error' : 'idle'}
				type="submit"
				disabled={isPending}
			>
				<span className="inline-flex items-center gap-2">
					<Icon name="passkey" />
					<span>{passkeyMessage}</span>
				</span>
			</StatusButton>
			<div className="mt-2">
				<ErrorList errors={[error]} id="passkey-login-button-error" announce />
			</div>
		</form>
	)
}

export const meta: Route.MetaFunction = () => {
	return noIndexMeta('Log in · Syrcha')
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
